/**
 * Shared upload-size caps so the SPA upload helper and the server's
 * `/api/uploads/request-url` endpoint can never drift.
 *
 * `DEFAULT_MAX_UPLOAD_BYTES` (250 MB) is the general ceiling for documents
 * and other non-media direct uploads. It matches the ClamAV full-scan
 * budget (`StreamMaxLength` ≈ 268 MB): finalize downloads those bytes into
 * Node and scans them. Do **not** raise this just to fit longform video —
 * that would admit 1 GB PDFs and OOM Render.
 *
 * `DEFAULT_MAX_MEDIA_UPLOAD_BYTES` (2 GiB) is media-only (content-board
 * recordings, deal-room project videos, and `video/*` + `audio/*` at
 * presign). Finalize skips full-body ClamAV for oversized media after the
 * existing magic-header check so the original file stays in storage without
 * a Node buffer of the whole object. Matches the Supabase Storage global
 * file size limit (2 GB) so a ~20–30 min / ~1–2 GB deal-room master is not
 * rejected by the app after the bucket was raised.
 *
 * Hosted deployments may raise/lower the general cap via
 * `UPLOAD_MAX_SCANNABLE_BYTES` and the media cap via
 * `UPLOAD_MAX_MEDIA_BYTES`. The SPA falls back to these defaults when
 * `/api/uploads/limits` is unreachable.
 */
export const DEFAULT_MAX_UPLOAD_BYTES = 250 * 1024 * 1024; // 250 MiB

export const DEFAULT_MAX_UPLOAD_LABEL = "250 MB";

/** 2 GiB — deal-room longform + content-board media; matches Storage global. */
export const DEFAULT_MAX_MEDIA_UPLOAD_BYTES = 2 * 1024 * 1024 * 1024;

export const DEFAULT_MAX_MEDIA_UPLOAD_LABEL = "2 GB";

/**
 * Headshot originals (team profiles, contacts, self-service "My Profile").
 * The server downscales to a 400px thumbnail before storage, so this cap
 * only covers the brief in-memory scan of a camera JPEG. It must match
 * every headshot uploader's client copy (`HEADSHOT_MAX_LABEL`) and the
 * `headshotUpload` multer limit — logo uploads stay on the smaller
 * `imageUpload` cap and must not share this value.
 */
export const HEADSHOT_MAX_BYTES = 25 * 1024 * 1024; // 25 MiB

/**
 * Render a byte count as a friendly "X MB" / "X.Y GB" string for use in
 * user-facing error messages. Stays deliberately simple: the only
 * callers are upload size errors, where exact precision doesn't matter.
 */
export function formatUploadBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return `${bytes} bytes`;
  const KB = 1024;
  const MB = KB * 1024;
  const GB = MB * 1024;
  if (bytes >= GB) {
    const gb = bytes / GB;
    return `${gb >= 10 ? Math.round(gb) : gb.toFixed(1)} GB`;
  }
  if (bytes >= MB) {
    const mb = bytes / MB;
    return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`;
  }
  if (bytes >= KB) {
    return `${Math.round(bytes / KB)} KB`;
  }
  return `${bytes} B`;
}

/**
 * Returns a friendly error string when `file` is larger than the
 * documented client-side cap, or `null` when the file is acceptable.
 *
 * The server still does the authoritative check in
 * `finalizeDirectUpload`; this just lets us reject obvious oversize
 * picks before any network request is made.
 */
export function getOversizedFileMessage(
  file: { name: string; size: number },
  maxBytes: number = DEFAULT_MAX_UPLOAD_BYTES,
): string | null {
  if (!Number.isFinite(file.size) || file.size <= maxBytes) return null;
  return `File "${file.name}" is ${formatUploadBytes(file.size)}, which exceeds the ${formatUploadBytes(maxBytes)} upload limit.`;
}

/** True when a declared MIME is a content-recording media type. */
export function isMediaRecordingContentType(contentType: string | undefined | null): boolean {
  if (!contentType) return false;
  const ct = contentType.toLowerCase().split(";")[0]?.trim() ?? "";
  return ct.startsWith("video/") || ct.startsWith("audio/");
}
